import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Radio, Spin, Typography } from '@arco-design/web-react';

import { formatCapacityForDisplay } from '../quantityUnit';
import { fetchVendorDeviceBillOptions } from './api';
import { formatOptionReferencePriceDisplay } from './formatReferencePrice';
import styles from './index.module.less';
import type { VendorAndDeviceRadioListProps, VendorDeviceBillOption, VendorDeviceCompositionItem, VendorDeviceSelection } from './types';
import {
  filterVendorDeviceOptionsByDeviceType,
  findVendorDeviceOption,
  formatVendorDeviceCellLabel,
  optionToSelection,
  resolveSkipFetchOptions,
  selectionToOptionKey,
} from './utils';

function formatOptionReferencePrice(option: VendorDeviceBillOption, lang?: string): import('react').ReactNode {
  const display = formatOptionReferencePriceDisplay(option, lang);
  if (display.lines?.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {display.lines.map((line) => (
          <div key={line.code} style={{ lineHeight: 1.45 }}>
            <span style={{ color: 'var(--color-text-3)', marginRight: 4 }}>{line.code}:</span>
            <span style={{ color: 'var(--color-text-1)' }}>{line.text}</span>
          </div>
        ))}
      </div>
    );
  }
  return display.single || '-';
}

function formatCompositionCell(composition?: VendorDeviceCompositionItem[]): import('react').ReactNode {
  if (!composition?.length) return '-';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {composition.map((item, index) => {
        const name = item.name || item.code;
        const total = item.quantity?.total;
        const unit = item.quantity?.unit?.default || '';
        const totalStr = total != null ? formatCapacityForDisplay(total, unit, item.quantity?.unit?.supported) || `${total}${unit}` : '';
        const device = item.device ? ` ${formatVendorDeviceCellLabel(item.device)}` : '';
        return (
          <div key={index} style={{ lineHeight: 1.5 }}>
            <span style={{ color: 'var(--color-text-3)', marginRight: 4 }}>{name}:</span>
            <span style={{ color: 'var(--color-text-1)' }}>
              {device ? `${device} ` : ''}
              {totalStr ? `×${totalStr}` : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function resolveSelectedId(options: VendorDeviceBillOption[], value?: VendorDeviceSelection | null): string | undefined {
  return findVendorDeviceOption(options, value)?.id;
}

/** 计价厂商设备单选列表：计费模型 id + 交付类型 id + 资源 code 查询；skipFetch 时仅展示预置 */
export function VendorAndDeviceRadioList({
  billModelId,
  deploymentTypeId,
  resourceCode,
  deviceType,
  value,
  onChange,
  onOptionsChange,
  mapOptions,
  disabled = false,
  showPricing = true,
  showSubResources = true,
  className,
  hideTitle = false,
  skipFetch = false,
  presetOptions,
  required = true,
  vendorLabel,
  deviceLabel,
  compactBottom = false,
}: VendorAndDeviceRadioListProps) {
  const { t, i18n } = useTranslation();
  const resolvedVendorLabel = vendorLabel ?? t('product:resource.vendor', { defaultValue: '厂商' });
  const resolvedDeviceLabel = deviceLabel ?? t('product:resource.device', { defaultValue: '设备' });
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<VendorDeviceBillOption[]>([]);
  const onOptionsChangeRef = useRef(onOptionsChange);
  onOptionsChangeRef.current = onOptionsChange;
  const mapOptionsRef = useRef(mapOptions);
  mapOptionsRef.current = mapOptions;

  const applyMappedOptions = async (raw: VendorDeviceBillOption[]) => {
    const mapper = mapOptionsRef.current;
    if (!mapper) return raw;
    return Promise.resolve(mapper(raw));
  };

  /** skipFetch 合成依赖：避免父组件每次 render 传入新 value 对象导致重复 setState */
  const skipFetchValueKey = useMemo(() => {
    if (!skipFetch) return '';
    const key = selectionToOptionKey(value) ?? '';
    const detail = String(value?.detail ?? '');
    const billConfig = String((value as { billConfig?: string } | null | undefined)?.billConfig ?? '');
    return `${key}::${detail}::${billConfig}`;
  }, [skipFetch, value]);

  useEffect(() => {
    if (!skipFetch) return;
    let cancelled = false;
    const raw = filterVendorDeviceOptionsByDeviceType(resolveSkipFetchOptions(value, resourceCode, presetOptions), deviceType);
    setLoading(true);
    void applyMappedOptions(raw)
      .then((next) => {
        if (cancelled) return;
        setOptions(next);
        onOptionsChangeRef.current?.(next);
      })
      .catch((error) => {
        console.error(error);
        if (cancelled) return;
        setOptions([]);
        onOptionsChangeRef.current?.([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 以 skipFetchValueKey 稳定 value 内容
  }, [skipFetch, skipFetchValueKey, resourceCode, deviceType, presetOptions]);

  useEffect(() => {
    if (skipFetch) return;

    if (!billModelId || !resourceCode || !deploymentTypeId) {
      setOptions([]);
      onOptionsChangeRef.current?.([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchVendorDeviceBillOptions(billModelId, resourceCode, deploymentTypeId)
      .then(async (items) => {
        if (cancelled) return;
        const filtered = filterVendorDeviceOptionsByDeviceType(items, deviceType);
        const next = await applyMappedOptions(filtered);
        if (cancelled) return;
        setOptions(next);
        onOptionsChangeRef.current?.(next);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setOptions([]);
          onOptionsChangeRef.current?.([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [billModelId, deploymentTypeId, resourceCode, deviceType, skipFetch]);

  const selectedId = useMemo(() => resolveSelectedId(options, value), [options, value]);

  const handleSelect = (option: VendorDeviceBillOption) => {
    if (disabled) return;
    onChange?.(optionToSelection(option));
  };

  const hasComposition = useMemo(
    () => showSubResources && options.some((o) => o.composition && o.composition.length > 0),
    [options, showSubResources],
  );

  const gridStyle = useMemo(() => {
    let cols = '48px 1fr 1fr';
    if (showPricing) cols += ' 1.2fr';
    if (hasComposition) cols += ' 1.5fr';
    return { gridTemplateColumns: cols };
  }, [showPricing, hasComposition]);

  const tableClassName = showPricing ? styles.table : `${styles.table} ${styles.noPricing}`;

  return (
    <div className={[styles.vendorSection, compactBottom ? styles.vendorSectionCompact : '', className].filter(Boolean).join(' ')}>
      {!hideTitle ? (
        <div className={styles.label}>
          {required ? <span className={styles.requiredMark}>*</span> : null}
          {t('data-center:vendor.device.selection', { defaultValue: '厂商设备选择' })}:
        </div>
      ) : null}
      <Spin loading={loading} style={{ width: '100%' }}>
        {!billModelId ? (
          <Typography.Text type="secondary">
            {t('data-center:please.select.billing.model.first', { defaultValue: '请先选择计费模型' })}
          </Typography.Text>
        ) : (
          <div className={tableClassName}>
            <div className={styles.header} style={gridStyle}>
              <div className={styles.radioCell} />
              <div className={styles.cell}>{resolvedVendorLabel}</div>
              <div className={styles.cell}>{resolvedDeviceLabel}</div>
              {hasComposition ? <div className={styles.cell}>{t('data-center:resource.composition', { defaultValue: '资源' })}</div> : null}
              {showPricing ? <div className={styles.cell}>{t('data-center:reference.price', { defaultValue: '参考价格' })}</div> : null}
            </div>
            {options.length ? (
              options.map((option) => {
                const selected = selectedId === option.id;
                return (
                  <div
                    key={option.id}
                    className={selected ? `${styles.row} ${styles.rowSelected}` : styles.row}
                    style={gridStyle}
                    onClick={() => handleSelect(option)}
                  >
                    <div className={styles.radioCell}>
                      <Radio checked={selected} disabled={disabled} onChange={() => handleSelect(option)} />
                    </div>
                    <div className={styles.cell}>{formatVendorDeviceCellLabel(option.vendor)}</div>
                    <div className={styles.cell}>{formatVendorDeviceCellLabel(option.device)}</div>
                    {hasComposition ? <div className={styles.cell}>{formatCompositionCell(option.composition)}</div> : null}
                    {showPricing ? <div className={styles.cell}>{formatOptionReferencePrice(option, i18n.language)}</div> : null}
                  </div>
                );
              })
            ) : (
              <div className={styles.empty}>{t('common:no.data', { defaultValue: '暂无数据' })}</div>
            )}
          </div>
        )}
      </Spin>
    </div>
  );
}

export type { VendorAndDeviceRadioListProps, VendorDeviceBillOption, VendorDeviceSelection } from './types';
export { optionToSelection, selectionToOptionKey } from './utils';
